import { createConfig, http } from 'wagmi';
import { base, baseSepolia, foundry } from 'wagmi/chains';
import { coinbaseWallet, injected, walletConnect } from 'wagmi/connectors';
import type { Chain } from 'viem';

/**
 * Robinhood Chain is not in `wagmi/chains` yet, so it is declared here. Ids and
 * endpoints come from the public docs; treat them as provisional.
 */
export const robinhood = {
  id: 4663,
  name: 'Robinhood',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: ['https://rpc.robinhood.com'] } },
  blockExplorers: {
    default: { name: 'Robinhood Explorer', url: 'https://explorer.robinhood.com' },
  },
  testnet: false,
} as const satisfies Chain;

export const robinhoodSepolia = {
  id: 46632,
  name: 'Robinhood Sepolia',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: ['https://rpc-sepolia.robinhood.com'] } },
  blockExplorers: {
    default: { name: 'Robinhood Sepolia Explorer', url: 'https://explorer-sepolia.robinhood.com' },
  },
  testnet: true,
} as const satisfies Chain;

/** Local anvil, used by `pnpm seed:local` to fill a chain with test tokens. */
export const localhost = {
  ...foundry,
  name: 'Localhost',
} as const satisfies Chain;

export const supportedChains = [
  base,
  baseSepolia,
  robinhood,
  robinhoodSepolia,
  localhost,
] as const;

export type SupportedChain = (typeof supportedChains)[number];
export type SupportedChainId = SupportedChain['id'];

const env = import.meta.env;
const walletConnectProjectId = env.VITE_WALLETCONNECT_PROJECT_ID as string | undefined;

/**
 * One generic `injected()` connector rather than `injected({target:'metaMask'})`
 * *plus* `metaMask()`: those two both report the name "MetaMask", which surfaced
 * as a duplicated button in the connect modal. The generic connector names itself
 * after whatever provider is actually installed.
 *
 * WalletConnect is only registered when a real project id is configured — the
 * placeholder id makes its relay reject the handshake and spam the console.
 */
const connectors = [
  injected({ shimDisconnect: true }),
  coinbaseWallet({ appName: 'bull.markets', preference: 'all' }),
  ...(walletConnectProjectId && walletConnectProjectId !== 'demo-project-id'
    ? [walletConnect({ projectId: walletConnectProjectId, showQrModal: true })]
    : []),
];

export const config = createConfig({
  chains: supportedChains,
  connectors,
  transports: {
    [base.id]: http(env.VITE_RPC_BASE as string | undefined),
    [baseSepolia.id]: http(env.VITE_RPC_BASE_SEPOLIA as string | undefined),
    [robinhood.id]: http(env.VITE_RPC_ROBINHOOD as string | undefined),
    [robinhoodSepolia.id]: http(env.VITE_RPC_ROBINHOOD_SEPOLIA as string | undefined),
    [localhost.id]: http('http://127.0.0.1:8545'),
  },
  ssr: false,
});

declare module 'wagmi' {
  interface Register {
    config: typeof config;
  }
}
